/*
 * Created on Wed Nov 01 2023 4:27:51 PM
 *
 * ***************************************************************************************
 * Copyright (c) AVPS Technologies Inc 2023 
 *
 * All Rights Reserved
 * Unpublished rights are reserved under the copyright laws of the United States.
 * The software contained in this media is proprietary to and embodies confidential
 * the technology of AVPS Technologies Inc. Possession, use, duplication or
 * dissemination of the software and media is authorized only pursuant to a valid
 * written license from AVPS Technologies Inc.
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * RESTRICTED RIGHTS LEGEND Use, duplication, or disclosure by the U.S. Government
 * is subject to restrictions as set forth in Subparagraph (c) (1) (ii) of DFARS
 * 252.227-7013 or in FAR 52.227-19, as applicable.
 ****************************************************************************************
 */
import React from 'react';
import {
  Box,
  IconButton,
  Drawer,
  Typography,
  TextField,
  Button,
  Chip,
  Tooltip,
  Fade,
  Zoom,
  Card,
  CardContent,
  LinearProgress,
  Avatar,
  Grid,
  Alert
} from 'components/ui/MaterialAdapter';
import {
  Close,
  Send,
  ExpandLess,
  ExpandMore,
  Minimize,
  AutoAwesome,
  Psychology,
  Lightbulb,
  History,
  Delete,
  ContentCopy,
  Business,
  Search,
  List as ListIcon,
  Computer,
  School,
  Store,
  BusinessCenter,
  DesignServices,
  TravelExplore,
  Code,
  CorporateFare,
  Domain,
  AccountTree,
  Analytics,
} from 'components/ui/IconAdapter';
import { useSelector } from 'react-redux';

const API_BASE_URL = 'http://localhost:5001';

const domainIcons = {
  EDUCATION: <School sx={{ color: '#10b981' }} />,
  RETAIL: <Store sx={{ color: '#f59e0b' }} />,
  INSTITUTION: <BusinessCenter sx={{ color: '#8b5cf6' }} />,
  FLEX_OFFICE: <CorporateFare sx={{ color: '#3b82f6' }} />,
  SERVICES: <DesignServices sx={{ color: '#ef4444' }} />,
  TRAVEL: <TravelExplore sx={{ color: '#06b6d4' }} />,
  IT_AND_CONSULTING: <Code sx={{ color: '#84cc16' }} />,
  IT: <Computer sx={{ color: '#f97316' }} />,
  ASSESSMENT: <Psychology sx={{ color: '#ec4899' }} />
};

const domainColors = {
  EDUCATION: '#10b981',
  RETAIL: '#f59e0b',
  INSTITUTION: '#8b5cf6',
  FLEX_OFFICE: '#3b82f6',
  SERVICES: '#ef4444',
  TRAVEL: '#06b6d4',
  IT_AND_CONSULTING: '#84cc16',
  IT: '#f97316',
  ASSESSMENT: '#ec4899'
};

const AISidebar = ({ open, onClose }) => {
  const [message, setMessage] = React.useState('');
  const [conversation, setConversation] = React.useState([
    {
      id: 1,
      type: 'ai',
      content: 'Hello! I\'m your AI assistant specialized in business domain exploration. I can help you discover, search, and analyze business domains across various industries.',
      timestamp: new Date()
    }
  ]);
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [isMiniMode, setIsMiniMode] = React.useState(false);
  const [isTyping, setIsTyping] = React.useState(false);
  const [agentStatus, setAgentStatus] = React.useState('online');

  const companyState = useSelector((state) => state.companyReducer.company);
  const type = companyState?.domain?.type;
  const isITCompany = type === "IT";

  // Domain-specific capabilities
  const domainCapabilities = [
    {
      name: "Domain Discovery",
      description: "Explore business domains by industry",
      icon: <Domain />,
      color: "#3b82f6",
      examples: ["technology", "retail", "education"]
    },
    {
      name: "Advanced Search", 
      description: "Find domains with specific criteria",
      icon: <Search />,
      color: "#8b5cf6",
      examples: ["consulting services", "software development"]
    },
    {
      name: "Industry Analysis",
      description: "Understand domain characteristics",
      icon: <Analytics />,
      color: "#10b981",
      examples: ["domain types", "business models"]
    },
    {
      name: "Organization View",
      description: "Explore company structures",
      icon: <AccountTree />,
      color: "#f59e0b",
      examples: ["corporate domains", "enterprise structure"]
    }
  ];

  // Enhanced quick actions with domain focus
  const quickActions = React.useMemo(() => [
    { 
      label: "All Domains", 
      prompt: "", 
      icon: <ListIcon />, 
      type: "domain",
      color: "#64748b",
      description: "Browse all business domains"
    },
    { 
      label: "Technology", 
      prompt: "technology", 
      icon: <Computer />, 
      type: "domain",
      color: "#3b82f6",
      description: "IT and software domains"
    },
    { 
      label: "Retail", 
      prompt: "retail", 
      icon: <Store />, 
      type: "domain",
      color: "#f59e0b",
      description: "Retail and commerce"
    },
    { 
      label: "Education", 
      prompt: "education", 
      icon: <School />, 
      type: "domain",
      color: "#10b981",
      description: "Educational institutions"
    },
    { 
      label: "Services", 
      prompt: "services", 
      icon: <DesignServices />, 
      type: "domain",
      color: "#ef4444",
      description: "Service-based businesses"
    },
    { 
      label: "Consulting", 
      prompt: "consulting", 
      icon: <Business />, 
      type: "domain",
      color: "#8b5cf6",
      description: "Consulting firms"
    },
    { 
      label: "Travel", 
      prompt: "travel", 
      icon: <TravelExplore />, 
      type: "domain",
      color: "#06b6d4",
      description: "Travel and hospitality"
    },
    { 
      label: "Flex Office", 
      prompt: "flex office", 
      icon: <BusinessCenter />, 
      type: "domain",
      color: "#84cc16",
      description: "Flexible workspace providers"
    }
  ], []);

  // Enhanced quick prompts for domain exploration
  const quickPrompts = React.useMemo(() => [
    {
      text: "Show me technology domains",
      agent: "DomainAgent",
      icon: <Computer sx={{ fontSize: 16 }} />
    },
    {
      text: "Find retail business domains",
      agent: "DomainAgent", 
      icon: <Store sx={{ fontSize: 16 }} />
    },
    {
      text: "Search for education domains",
      agent: "DomainAgent",
      icon: <School sx={{ fontSize: 16 }} />
    },
    {
      text: "Show consulting services",
      agent: "DomainAgent",
      icon: <Business sx={{ fontSize: 16 }} />
    },
    {
      text: "Find travel and hospitality",
      agent: "DomainAgent",
      icon: <TravelExplore sx={{ fontSize: 16 }} />
    },
    {
      text: "Explore service businesses",
      agent: "DomainAgent",
      icon: <DesignServices sx={{ fontSize: 16 }} />
    },
    {
      text: "Show all domain types",
      agent: "DomainAgent", 
      icon: <ListIcon sx={{ fontSize: 16 }} />
    },
    {
      text: "Find IT consulting domains",
      agent: "DomainAgent",
      icon: <Code sx={{ fontSize: 16 }} />
    }
  ], []);

  // Enhanced API call with better error handling
  const searchBusinessDomains = async (searchQuery) => {
    try {
      setAgentStatus('searching');
      
      const url = searchQuery 
        ? `${API_BASE_URL}/business-domains?search=${encodeURIComponent(searchQuery)}`
        : `${API_BASE_URL}/business-domains`;
        
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      throw error;
    } finally {
      setAgentStatus('online');
    }
  };

  // Safe domain extraction from API response
  const extractDomainsFromResponse = (data) => {
    if (!data) return [];
    
    if (Array.isArray(data)) return data;
    if (data.domains && Array.isArray(data.domains)) return data.domains;
    if (data.data && Array.isArray(data.data)) return data.data;
    if (data.results && Array.isArray(data.results)) return data.results;
    if (typeof data === 'object' && !Array.isArray(data)) return [data];
    
    return [];
  };

  // Enhanced domain formatting with vibrant design
  const formatDomainResponse = (data, searchQuery = '') => {
    const domains = extractDomainsFromResponse(data);
    
    if (domains.length === 0) {
      return (
        <Box>
          <Alert severity="info" sx={{ 
            mb: 2, 
            backgroundColor: '#dbeafe',
            border: '1px solid #3b82f6',
            borderRadius: 2
          }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Search sx={{ fontSize: 18 }} />
              No Business Domains Found
            </Typography>
          </Alert>
          <Card sx={{ 
            backgroundColor: '#f8fafc', 
            border: '2px dashed #cbd5e1', 
            textAlign: 'center', 
            py: 4,
            borderRadius: 2
          }}>
            <Typography variant="body1" sx={{ color: '#64748b', mb: 2 }}>
              {searchQuery 
                ? `No domains found for "${searchQuery}"`
                : 'No business domains available'
              }
            </Typography>
            <Button 
              variant="outlined" 
              onClick={() => handleQuickAction("")}
              sx={{ 
                borderColor: '#3b82f6', 
                color: '#3b82f6',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: '#3b82f6',
                  color: 'white'
                }
              }}
            >
              Browse All Domains
            </Button>
          </Card>
        </Box>
      );
    }

    return (
      <Box>
        <Alert severity="success" sx={{ 
          mb: 3, 
          backgroundColor: '#d1fae5',
          border: '1px solid #10b981',
          borderRadius: 2
        }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Domain sx={{ fontSize: 18 }} />
            Found {domains.length} Business Domain(s)
            {searchQuery && ` for "${searchQuery}"`}
          </Typography>
        </Alert>

        <Grid container spacing={2}>
          {domains.map((domain, index) => {
            const domainType = domain.type || domain.domain_type;
            const domainColor = domainColors[domainType] || '#64748b';
            
            return (
              <Grid item xs={12} key={domain.primaryId || index}>
                <Card
                  variant="outlined"
                  sx={{
                    border: `2px solid ${domainColor}20`,
                    borderRadius: 3,
                    backgroundColor: 'white',
                    transition: 'all 0.3s ease',
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                    '&:hover': {
                      borderColor: domainColor,
                      boxShadow: `0 8px 25px ${domainColor}30`,
                      transform: 'translateY(-2px)',
                    }
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      {/* Domain Icon */}
                      <Box sx={{
                        backgroundColor: `${domainColor}15`,
                        borderRadius: 2,
                        p: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: `1px solid ${domainColor}30`
                      }}>
                        {domainIcons[domainType] || <Business sx={{ fontSize: 24, color: domainColor }} />}
                      </Box>

                      {/* Domain Content */}
                      <Box sx={{ flex: 1 }}>
                        {/* Header */}
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
                          <Box>
                            <Typography variant="subtitle1" sx={{ 
                              fontWeight: 800, 
                              color: '#1e293b',
                              fontSize: '1.1rem'
                            }}>
                              {domain.name || domain.domain_name || domain.domain || 'Unnamed Domain'}
                            </Typography>
                            <Typography variant="caption" sx={{ 
                              color: '#64748b',
                              fontWeight: 600
                            }}>
                              {domain.code || domain.domain_code || 'No Code'}
                            </Typography>
                          </Box>
                          
                          {/* Domain Type Badge */}
                          {domainType && (
                            <Chip
                              label={domainType.replace(/_/g, ' ')}
                              size="small"
                              sx={{
                                fontSize: '0.7rem',
                                fontWeight: 700,
                                backgroundColor: `${domainColor}15`,
                                color: domainColor,
                                border: `1px solid ${domainColor}30`,
                                height: 24
                              }}
                            />
                          )}
                        </Box>

                        {/* Description */}
                        <Typography variant="body2" sx={{
                          color: '#64748b',
                          lineHeight: 1.6,
                          fontSize: '0.9rem',
                          mb: 2
                        }}>
                          {domain.description || domain.domain_description || `A ${domainType ? domainType.toLowerCase().replace('_', ' ') : 'business'} domain offering specialized services and solutions.`}
                        </Typography>

                        {/* Additional Info */}
                        <Box sx={{ 
                          display: 'flex', 
                          gap: 2,
                          flexWrap: 'wrap'
                        }}>
                          {domain.category && (
                            <Chip
                              label={`Category: ${domain.category}`}
                              size="small"
                              variant="outlined"
                              sx={{
                                fontSize: '0.7rem',
                                backgroundColor: '#f1f5f9'
                              }}
                            />
                          )}
                          
                          {domain.industry && (
                            <Chip
                              label={`Industry: ${domain.industry}`}
                              size="small"
                              variant="outlined"
                              sx={{
                                fontSize: '0.7rem',
                                backgroundColor: '#f1f5f9'
                              }}
                            />
                          )}
                        </Box>

                        {/* Subdomains */}
                        {domain.subdomains && domain.subdomains.length > 0 && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="caption" sx={{ 
                              fontWeight: 700, 
                              color: '#475569',
                              display: 'block',
                              mb: 1
                            }}>
                              Related Areas:
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {domain.subdomains.slice(0, 4).map((subdomain, subIndex) => (
                                <Chip
                                  key={subIndex}
                                  label={subdomain}
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    fontSize: '0.65rem',
                                    height: 20,
                                    backgroundColor: '#f8fafc',
                                    borderColor: '#e2e8f0'
                                  }}
                                />
                              ))}
                              {domain.subdomains.length > 4 && (
                                <Chip
                                  label={`+${domain.subdomains.length - 4} more`}
                                  size="small"
                                  sx={{
                                    fontSize: '0.65rem',
                                    height: 20,
                                    backgroundColor: '#f1f5f9'
                                  }}
                                />
                              )}
                            </Box>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        <Alert severity="info" sx={{ 
          mt: 3,
          backgroundColor: '#f0f9ff',
          border: '1px solid #0ea5e9'
        }}>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            💡 Pro Tip: Use specific industry keywords like "technology", "retail", or "consulting" to find relevant domains faster.
          </Typography>
        </Alert>
      </Box>
    );
  };

  // Enhanced text response with agent attribution
  const formatTextResponse = (content, agent = 'Domain Agent') => (
    <Box>
      <Typography variant="body2" sx={{ 
        lineHeight: 1.6, 
        whiteSpace: 'pre-wrap',
        mb: 1
      }}>
        {content}
      </Typography>
      <Chip
        label={`via ${agent}`}
        size="small"
        sx={{
          backgroundColor: '#f1f5f9',
          color: '#64748b',
          fontSize: '0.7rem',
          height: 20,
          fontWeight: 600
        }}
      />
    </Box>
  );

  const getDisplayContent = (msg) => {
    if (msg.domains || msg.data) {
      return formatDomainResponse(msg.data || msg.domains, msg.searchQuery);
    }
    return formatTextResponse(msg.content, msg.agent);
  };

  const handleSendMessage = async (searchQuery = null) => {
    const queryToSend = searchQuery !== null ? searchQuery : message;
    
    if (queryToSend.trim() === '' && searchQuery === null) return;

    const userMessage = {
      id: conversation.length + 1,
      type: 'user',
      content: searchQuery !== null 
        ? queryToSend 
          ? `Search for "${queryToSend}" domains` 
          : 'Show all domains'
        : queryToSend,
      timestamp: new Date()
    };

    setConversation(prev => [...prev, userMessage]);
    if (searchQuery === null) {
      setMessage('');
    }
    setIsTyping(true);
    setAgentStatus('searching');

    try {
      const responseData = await searchBusinessDomains(queryToSend);
      const domains = extractDomainsFromResponse(responseData);
      
      const aiResponse = {
        id: conversation.length + 2,
        type: 'ai',
        content: domains.length > 0 
          ? queryToSend 
            ? `Found ${domains.length} domain(s) matching "${queryToSend}"`
            : `Found ${domains.length} business domains`
          : queryToSend
            ? `No domains found for "${queryToSend}"`
            : 'No business domains available',
        timestamp: new Date(),
        agent: 'Domain Explorer',
        data: responseData,
        searchQuery: queryToSend,
        domains: domains
      };

      setConversation(prev => [...prev, aiResponse]);
    } catch (error) {
      const errorMessage = {
        id: conversation.length + 2,
        type: 'ai',
        content: 'Sorry, I encountered an error while searching for business domains. Please check if the server is running.',
        timestamp: new Date(),
        agent: 'Error Handler'
      };
      setConversation(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
      setAgentStatus('online');
    }
  };

  const handleQuickAction = (prompt) => {
    handleSendMessage(prompt);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const clearConversation = () => {
    setConversation([{
      id: 1,
      type: 'ai',
      content: 'Hello! I\'m your AI assistant specialized in business domain exploration. I can help you discover, search, and analyze business domains across various industries.',
      timestamp: new Date()
    }]);
  };

  const copyLastResponse = () => {
    const lastAIResponse = [...conversation].reverse().find(msg => msg.type === 'ai');
    if (lastAIResponse) {
      navigator.clipboard.writeText(lastAIResponse.content);
    }
  };

  const toggleMiniMode = () => {
    setIsMiniMode(!isMiniMode);
    if (!isMiniMode) {
      setIsExpanded(false);
    }
  };

  const messagesEndRef = React.useRef(null);

  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [conversation]);

  const getAgentStatusText = () => {
    switch (agentStatus) {
      case 'searching': return 'Exploring Domains...';
      case 'processing': return 'Analyzing...';
      default: return 'Ready';
    }
  };

  // Calculate dynamic heights for proper scrolling
  const getMessagesContainerHeight = () => {
    if (isExpanded) {
      return 'calc(100vh - 520px)'; // Adjusted for expanded view with capabilities section
    }
    return 'calc(100vh - 240px)'; // Standard height for collapsed view
  };

  return (
    <>
      {/* Mini Chat Mode */}
      {isMiniMode && (
        <Fade in={isMiniMode} timeout={400}>
          <Box
            sx={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              width: 360,
              height: 480,
              backgroundColor: 'white',
              borderRadius: 3,
              boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
              display: 'flex',
              flexDirection: 'column',
              zIndex: 9999,
              overflow: 'hidden',
              border: '1px solid #e2e8f0'
            }}
          >
            {/* Mini Header */}
            <Box sx={{
              p: 2,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    backgroundColor: 'rgba(255,255,255,0.2)',
                  }}
                >
                  <Domain sx={{ fontSize: 18 }} />
                </Avatar>
                <Box>
                  <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 700 }}>
                    Domain Explorer
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', fontWeight: 600 }}>
                    {getAgentStatusText()}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Tooltip title="Expand" arrow>
                  <IconButton
                    size="small"
                    onClick={() => setIsMiniMode(false)}
                    sx={{
                      color: 'white',
                      backgroundColor: 'rgba(255,255,255,0.2)',
                      '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' },
                    }}
                  >
                    <ExpandMore fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Close" arrow>
                  <IconButton
                    size="small"
                    onClick={onClose}
                    sx={{
                      color: 'white',
                      backgroundColor: 'rgba(255,255,255,0.2)',
                      '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' },
                    }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {/* Mini Messages Area */}
            <Box sx={{
              flex: 1,
              p: 2,
              overflowY: 'auto',
              backgroundColor: '#f8fafc',
            }}>
              {conversation.slice(-4).map((msg) => (
                <Box
                  key={msg.id}
                  sx={{
                    mb: 1.5,
                    display: 'flex',
                    justifyContent: msg.type === 'user' ? 'flex-end' : 'flex-start'
                  }}
                >
                  <Box
                    sx={{
                      maxWidth: '80%',
                      p: 1.5,
                      borderRadius: 2,
                      backgroundColor: msg.type === 'user' 
                        ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                        : 'white',
                      color: msg.type === 'user' ? 'white' : '#1e293b',
                      border: msg.type === 'user' ? 'none' : '1px solid #e2e8f0',
                      fontSize: '0.8rem',
                    }}
                  >
                    {msg.type === 'ai' && msg.domains ? (
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>
                        Found {msg.domains.length} domain(s)
                      </Typography>
                    ) : (
                      <Typography variant="caption">
                        {msg.content.length > 100 ? msg.content.substring(0, 100) + '...' : msg.content}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
              {isTyping && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                  <Box sx={{ p: 1.5, borderRadius: 2, backgroundColor: 'white', border: '1px solid #e2e8f0' }}>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {[0, 1, 2].map((i) => (
                        <Box
                          key={i}
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            backgroundColor: '#8b5cf6',
                            animation: 'pulse 1.4s ease-in-out infinite',
                            animationDelay: `${i * 0.2}s`,
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>

            {/* Mini Input Area */}
            <Box sx={{ p: 2, borderTop: '1px solid #e2e8f0' }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search domains..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={isTyping}
                />
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => handleSendMessage()}
                  disabled={message.trim() === '' || isTyping}
                  sx={{
                    minWidth: 'auto',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  }}
                >
                  <Send fontSize="small" />
                </Button>
              </Box>
            </Box>
          </Box>
        </Fade>
      )}

      {/* Full Sidebar - Enhanced for Domain Search */}
      <Drawer
        anchor="right"
        open={open && !isMiniMode}
        onClose={onClose}
        sx={{
          '& .ui-drawer-paper': {
            width: isExpanded ? 600 : 420,
            maxWidth: '90vw',
            backgroundColor: 'white',
            boxShadow: '-8px 0 40px rgba(0,0,0,0.15)',
            borderLeft: '1px solid #e2e8f0',
            display: 'flex',
            flexDirection: 'column'
          },
        }}
      >
        {/* Enhanced Header */}
        <Box sx={{
          p: 3,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              sx={{
                width: 48,
                height: 48,
                backgroundColor: 'rgba(255,255,255,0.2)',
              }}
            >
              <Domain />
            </Avatar>

            <Box>
              <Typography variant="h6" sx={{ color: 'white', fontWeight: 800, lineHeight: 1.2 }}>
                {isITCompany ? "Krumago Domain AI" : "Business Domain Explorer"}
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 700, ml: 1, fontSize: '0.7rem' }}>
                  • {getAgentStatusText()}
                </Typography>
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.75rem' }}>
                Domain Discovery • Industry Search • Business Analysis
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title={isExpanded ? "Collapse" : "Expand"} arrow>
              <IconButton
                onClick={() => setIsExpanded(!isExpanded)}
                sx={{
                  color: 'white',
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' },
                }}
              >
                {isExpanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Mini mode" arrow>
              <IconButton
                onClick={toggleMiniMode}
                sx={{
                  color: 'white',
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' },
                }}
              >
                <Minimize />
              </IconButton>
            </Tooltip>
            <Tooltip title="Close" arrow>
              <IconButton
                onClick={onClose}
                sx={{
                  color: 'white',
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' },
                }}
              >
                <Close />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Agent Status Bar */}
        {agentStatus !== 'online' && (
          <Box sx={{ width: '100%', flexShrink: 0 }}>
            <LinearProgress 
              sx={{ 
                height: 3,
                backgroundColor: 'rgba(255,255,255,0.2)',
                '& .ui-linear-progress-bar': {
                  background: 'linear-gradient(90deg, #3b82f6, #8b5cf6)',
                  animation: 'pulse 2s ease-in-out infinite',
                }
              }} 
            />
          </Box>
        )}

        {/* Enhanced Expanded Sidebar Content */}
        {isExpanded && (
          <Zoom in={isExpanded} timeout={300}>
            <Box sx={{
              p: 3,
              borderBottom: '1px solid #e2e8f0',
              backgroundColor: '#fafafa',
              flexShrink: 0
            }}>
              {/* Domain Capabilities */}
              <Typography variant="subtitle1" sx={{ color: '#1e293b', mb: 2, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1 }}>
                <AutoAwesome sx={{ fontSize: 20, color: '#8b5cf6' }} />
                Domain Capabilities
              </Typography>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                {domainCapabilities.map((capability, index) => (
                  <Grid item xs={6} key={index}>
                    <Card sx={{ 
                      p: 1.5, 
                      backgroundColor: 'white',
                      border: `2px solid ${capability.color}20`,
                      borderRadius: 2,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: capability.color,
                        transform: 'translateY(-2px)',
                      }
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Box sx={{ color: capability.color }}>
                          {capability.icon}
                        </Box>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#1e293b' }}>
                          {capability.name}
                        </Typography>
                      </Box>
                      <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 1 }}>
                        {capability.description}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {capability.examples.slice(0, 2).map((example, i) => (
                          <Chip
                            key={i}
                            label={example}
                            size="small"
                            onClick={() => handleQuickAction(example)}
                            sx={{
                              height: 20,
                              fontSize: '0.6rem',
                              backgroundColor: `${capability.color}10`,
                              color: capability.color,
                              border: `1px solid ${capability.color}30`,
                              cursor: 'pointer'
                            }}
                          />
                        ))}
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {/* Quick Domain Searches */}
              <Typography variant="subtitle1" sx={{ color: '#1e293b', mb: 2, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Lightbulb sx={{ fontSize: 20, color: '#f59e0b' }} />
                Quick Domain Searches
              </Typography>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                {quickActions.map((action, index) => (
                  <Chip
                    key={index}
                    label={action.label}
                    size="small"
                    onClick={() => handleQuickAction(action.prompt)}
                    icon={React.cloneElement(action.icon, { sx: { fontSize: 16, color: action.color } })}
                    sx={{
                      cursor: 'pointer',
                      backgroundColor: 'white',
                      border: `2px solid ${action.color}30`,
                      color: '#1e293b',
                      fontWeight: 600,
                      '&:hover': {
                        backgroundColor: action.color,
                        color: 'white',
                        borderColor: action.color,
                        transform: 'translateY(-1px)',
                      },
                      transition: 'all 0.2s ease'
                    }}
                  />
                ))}
              </Box>

              {/* Enhanced Quick Prompts */}
              <Typography variant="subtitle1" sx={{ color: '#1e293b', mb: 2, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Psychology sx={{ fontSize: 20, color: '#3b82f6' }} />
                Try Searching...
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2.5 }}>
                {quickPrompts.map((prompt, index) => (
                  <Card
                    key={index}
                    sx={{
                      p: 1.5,
                      backgroundColor: 'white',
                      border: '1px solid #e2e8f0',
                      borderRadius: 2,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: '#3b82f6',
                        color: 'white',
                        transform: 'translateX(4px)',
                        '& .prompt-text, & .prompt-icon': {
                          color: 'white'
                        }
                      }
                    }}
                    onClick={() => handleQuickAction(prompt.text.replace(/^(show me|find|search for|explore)\s+/i, ''))}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box className="prompt-icon" sx={{ color: '#3b82f6' }}>
                        {prompt.icon}
                      </Box>
                      <Typography variant="body2" className="prompt-text" sx={{ color: '#64748b', fontWeight: 500, fontSize: '0.8rem' }}>
                        {prompt.text}
                      </Typography>
                    </Box>
                  </Card>
                ))}
              </Box>

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                <Tooltip title="Clear conversation" arrow>
                  <Button
                    size="small"
                    startIcon={<Delete />}
                    onClick={clearConversation}
                    sx={{
                      color: '#64748b',
                      backgroundColor: 'white',
                      border: '1px solid #e2e8f0',
                      borderRadius: 2,
                      fontWeight: 600,
                      '&:hover': {
                        backgroundColor: '#fee2e2',
                        borderColor: '#fca5a5',
                        color: '#dc2626',
                      }
                    }}
                  >
                    Clear
                  </Button>
                </Tooltip>
                <Tooltip title="Copy last response" arrow>
                  <Button
                    size="small"
                    startIcon={<ContentCopy />}
                    onClick={copyLastResponse}
                    sx={{
                      color: '#64748b',
                      backgroundColor: 'white',
                      border: '1px solid #e2e8f0',
                      borderRadius: 2,
                      fontWeight: 600,
                      '&:hover': {
                        backgroundColor: '#dbeafe',
                        borderColor: '#93c5fd',
                        color: '#2563eb',
                      }
                    }}
                  >
                    Copy
                  </Button>
                </Tooltip>
              </Box>
            </Box>
          </Zoom>
        )}

        {/* Enhanced Chat Messages - Fixed scrolling issue */}
        <Box sx={{
          flex: 1,
          p: 3,
          overflowY: 'auto',
          height: getMessagesContainerHeight(),
          backgroundColor: '#f8fafc',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#cbd5e1',
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: '#94a3b8',
            }
          }
        }}>
          {conversation.map((msg, index) => (
            <Fade in key={msg.id} timeout={400} style={{ transitionDelay: `${index * 50}ms` }}>
              <Box
                sx={{
                  mb: 2,
                  display: 'flex',
                  justifyContent: msg.type === 'user' ? 'flex-end' : 'flex-start'
                }}
              >
                <Box
                  sx={{
                    maxWidth: '85%',
                    p: 2.5,
                    borderRadius: 3,
                    backgroundColor: msg.type === 'user' 
                      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                      : 'white',
                    color: msg.type === 'user' ? 'white' : '#1e293b',
                    border: msg.type === 'user' ? 'none' : '1px solid #e2e8f0',
                    position: 'relative',
                    transition: 'all 0.3s ease',
                    boxShadow: msg.type === 'user' 
                      ? '0 4px 20px rgba(102, 126, 234, 0.3)'
                      : '0 4px 20px rgba(0,0,0,0.08)',
                    '&:hover': {
                      boxShadow: msg.type === 'user'
                        ? '0 6px 25px rgba(102, 126, 234, 0.4)'
                        : '0 6px 25px rgba(0,0,0,0.12)',
                      transform: 'translateY(-2px)',
                    }
                  }}
                >
                  {msg.type === 'ai' && (
                    <Box sx={{
                      position: 'absolute',
                      top: -12,
                      left: -12,
                      backgroundColor: 'white',
                      borderRadius: '50%',
                      p: 1,
                      border: '2px solid #e2e8f0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }}>
                      <Domain sx={{ fontSize: 20, color: '#8b5cf6' }} />
                    </Box>
                  )}

                  {getDisplayContent(msg)}

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1.5 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: msg.type === 'user' ? 'rgba(255,255,255,0.8)' : '#64748b',
                        fontWeight: 600,
                        fontSize: '0.7rem'
                      }}
                    >
                      {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </Typography>
                    {msg.type === 'ai' && msg.agent && (
                      <Chip
                        label={msg.agent}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.6rem',
                          backgroundColor: '#f1f5f9',
                          color: '#64748b',
                          fontWeight: 600
                        }}
                      />
                    )}
                  </Box>
                </Box>
              </Box>
            </Fade>
          ))}

          {/* Enhanced Typing Indicator */}
          {isTyping && (
            <Fade in timeout={300}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
                <Box sx={{
                  p: 2.5,
                  borderRadius: 3,
                  backgroundColor: 'white',
                  border: '1px solid #e2e8f0',
                  position: 'relative',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
                }}>
                  <Box sx={{
                    position: 'absolute',
                    top: -12,
                    left: -12,
                    backgroundColor: 'white',
                    borderRadius: '50%',
                    p: 1,
                    border: '2px solid #e2e8f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Domain sx={{ fontSize: 20, color: '#8b5cf6' }} />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ display: 'flex', gap: 0.75 }}>
                      {[0, 1, 2].map((i) => (
                        <Box
                          key={i}
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: '#8b5cf6',
                            animation: 'pulse 1.4s ease-in-out infinite',
                            animationDelay: `${i * 0.2}s`,
                            '@keyframes pulse': {
                              '0%, 60%, 100%': { opacity: 0.3, transform: 'scale(0.8)' },
                              '30%': { opacity: 1, transform: 'scale(1.2)' },
                            }
                          }}
                        />
                      ))}
                    </Box>
                    <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 600 }}>
                      {agentStatus === 'searching' ? 'Exploring business domains...' : 'Analyzing domain data...'}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Fade>
          )}
          <div ref={messagesEndRef} />
        </Box>

        {/* Enhanced Input Area */}
        <Box sx={{
          p: 3,
          borderTop: '1px solid #e2e8f0',
          backgroundColor: 'white',
          flexShrink: 0
        }}>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-end' }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search business domains by industry, type, or keyword..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              multiline
              maxRows={3}
              disabled={isTyping}
              sx={{
                '& .ui-outlined-input-root': {
                  backgroundColor: '#f8fafc',
                  borderRadius: 2,
                  '& fieldset': {
                    borderColor: '#e2e8f0',
                  },
                  '&:hover fieldset': {
                    borderColor: '#cbd5e1',
                  },
                  '&.ui-focused fieldset': {
                    borderColor: '#8b5cf6',
                    borderWidth: '2px',
                  },
                  '&.ui-disabled': {
                    backgroundColor: '#f1f5f9',
                  }
                }
              }}
            />
            <Button
              variant="contained"
              onClick={() => handleSendMessage()}
              disabled={message.trim() === '' || isTyping}
              sx={{
                minWidth: 'auto',
                px: 3,
                height: '56px',
                borderRadius: 2,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                fontWeight: 700,
                '&:hover': {
                  background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 6px 20px rgba(102, 126, 234, 0.4)',
                },
                '&:disabled': {
                  background: '#e2e8f0',
                  color: '#94a3b8',
                  transform: 'none',
                  boxShadow: 'none',
                },
                transition: 'all 0.2s ease'
              }}
            >
              <Send />
            </Button>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
            <Typography
              variant="caption"
              sx={{
                color: '#64748b',
                fontWeight: 600
              }}
            >
              {isTyping ? 'AI is exploring business domains...' : 'Search domains by industry, type, or keyword'}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: '#64748b',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                fontWeight: 700,
                backgroundColor: '#f1f5f9',
                px: 2,
                py: 1,
                borderRadius: 2
              }}
            >
              <History sx={{ fontSize: 14 }} />
              {conversation.filter(msg => msg.type === 'user').length} searches
            </Typography>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default AISidebar;
